import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useProductReadContext } from '@context/ProductContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body2, Caption1 } from '@ui/Typography'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { TableCell } from '@ui/v2/Table/Table'

import { formatDate, withTimeFormat } from '@utils/date.utils'
import { TransactionContext, getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'

import { useRelativeTime } from '@hooks/useRelativeTime'

import { AnimatedNumber } from '@shared/animations/AnimatedNumber'
import { Box } from '@shared/components/Box'
import { CopyButton } from '@shared/components/CopyButton/CopyButton'
import {
  TransactionHistoryFromToCell,
  getFromToCellData,
  normalizeFromToCellContent,
} from '@shared/components/TransactionHistoryFromToCell'
import { TransactionStatusItem } from '@shared/components/TransactionStatusItem'
import { TransactionTypeIcon } from '@shared/components/TransactionTypeIcon/TransactionTypeIcon'

import styles from './transactionHistoryTable.module.scss'

interface Props {
  tx: TransactionDto
  context: TransactionContext
  walletId?: string
}

export const TransactionHistoryRowCells: React.FC<Props> = ({ tx, context, walletId }) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const isArabic = useArabicSessionLanguage()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { isDefaultCFDProductType } = useProductReadContext()

  const { isReversed, fields, amountSign } = getTransactionDisplay(tx, context, {
    isArabic,
    authId: account?.id,
    isDefaultCFDProductType: isDefaultCFDProductType(),
    walletId,
  })

  const fromDirection = isReversed ? 'to' : 'from'
  const toDirection = isReversed ? 'from' : 'to'
  const fromCellData = getFromToCellData(tx, fromDirection)
  const toCellData = getFromToCellData(tx, toDirection)
  const fromCellContent = normalizeFromToCellContent(
    tx.type.id,
    fromDirection,
    fields.from,
    fromCellData.subtitle
  )
  const toCellContent = normalizeFromToCellContent(
    tx.type.id,
    toDirection,
    fields.to,
    toCellData.subtitle
  )
  const relativeStatusTime = useRelativeTime(tx.updatedDate || tx.createdDate)
  const createdDateWithTime = formatDate(tx.createdDate, { formatType: withTimeFormat(dateFormat) })

  return (
    <>
      <TableCell align='left' hasTruncate={false} className={styles.typeCell}>
        <Box flex align='center' gap='x8' fullWidth minWidth={0}>
          <TransactionTypeIcon transaction={tx} variant='neutral' />
          <div className={styles.typeCellContent}>
            <Truncate>
              <Body2 medium>{tx.type.name}</Body2>
            </Truncate>
            <SensitiveData>
              <Caption1 color='text-low-em'>{createdDateWithTime}</Caption1>
            </SensitiveData>
          </div>
        </Box>
      </TableCell>

      <TableCell align='right' hasTruncate={false} className={styles.amountRow}>
        <SensitiveData>
          <Body2 medium>
            {amountSign}
            <AnimatedNumber value={fields.amount} currency={fields.currency} />
          </Body2>
        </SensitiveData>
      </TableCell>

      <TableCell className={styles.amountSpacerCell} hasTruncate={false} />

      <TableCell align='left' hasTruncate={false} dir={isArabic ? 'ltr' : undefined}>
        <TransactionHistoryFromToCell
          hasTruncate
          label={fromCellContent.label}
          icon={fromCellData.icon}
          subtitle={fromCellContent.subtitle}
        />
      </TableCell>

      <TableCell align='center' className={styles.arrowCell} hasTruncate={false}>
        <ArrowIcon.Right size={20} color='text-base-em' />
      </TableCell>

      <TableCell align='left' hasTruncate={false} dir={isArabic ? 'ltr' : undefined}>
        <TransactionHistoryFromToCell
          hasTruncate
          label={toCellContent.label}
          icon={toCellData.icon}
          subtitle={toCellContent.subtitle}
        />
      </TableCell>

      <TableCell hasTruncate={false} align='left'>
        <TransactionStatusItem transaction={tx} subtitle={relativeStatusTime} />
      </TableCell>

      <TableCell align='left' className={styles.nameRow} hasTruncate={false} contentFullWidth>
        {tx.referenceId ? (
          <Box flex align='center' gap='x4' fullWidth minWidth={0}>
            <Box flexItem='1' minWidth={0}>
              <SensitiveData>
                <Truncate>
                  <span className={styles.referenceText}>{tx.referenceId}</span>
                </Truncate>
              </SensitiveData>
            </Box>
            <div onClick={(e) => e.stopPropagation()}>
              <CopyButton
                value={tx.referenceId}
                variant='ghost'
                size='sm'
                tooltipText={t('Copy')}
              />
            </div>
          </Box>
        ) : (
          <span className={styles.secondaryText}>{t('N/A')}</span>
        )}
      </TableCell>

      <TableCell align='center' hasTruncate={false}>
        <CaretIcon.Right color='text-low-em' size={24} />
      </TableCell>
    </>
  )
}
